/**
 * CollectionMetricStrip - Four metric cards with 24h rolling deltas
 *
 * Follows the Today page MetricStrip pattern.
 * Cards: Conduct Score, Dissatisfaction Ratio, Vulnerability Exposure, Eligible Complaints
 *
 * Atomic level: Organism
 */

import React from 'react';
import { Card, CardContent } from '../../../ui/card';
import { getConductScoreColor } from './ConductScoreIndicator';
import type { CollectionMetrics } from '../types/collectionMetrics.types';
import type { TrendDirection } from '../../today/types/today.types';

interface CollectionMetricStripProps {
  metrics: CollectionMetrics;
  className?: string;
}

interface MetricCardProps {
  label: string;
  value: number;
  unit?: string;
  delta: number;
  trend: TrendDirection;
  color?: string;
  invertTrend?: boolean;
}

const formatDelta = (delta: number, unit: string): string => {
  const sign = delta > 0 ? '↑' : delta < 0 ? '↓' : '';
  const abs = Math.abs(delta);
  const formatted = unit === '%' ? `${abs}%` : abs.toFixed(1);
  return `${sign} ${formatted} in last 24h`;
};

const getDeltaColor = (trend: TrendDirection, invertTrend: boolean): string => {
  if (trend === 'stable') return 'text-muted-foreground';
  const isUp = trend === 'up';
  // For most metrics, up = worse (bittersweet). For inverted, up = better (green).
  const isBad = invertTrend ? !isUp : isUp;
  return isBad ? 'text-[#FF7461]' : 'text-[#22C55E]';
};

const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  unit = '',
  delta,
  trend,
  color,
  invertTrend = false,
}) => {
  const displayValue = unit === '%' ? `${Math.round(value)}%` : value.toFixed(1);
  const deltaText = trend === 'stable' ? 'No change' : formatDelta(delta, unit);
  const deltaColor = getDeltaColor(trend, invertTrend);

  return (
    <Card className="bg-white border-slate-200">
      <CardContent className="p-5">
        <div
          className="text-[32px] font-extrabold leading-none tracking-tight"
          style={{ color: color || 'var(--foreground)' }}
        >
          {displayValue}
        </div>
        <div className="text-[11px] text-muted-foreground font-medium mt-1.5">
          {label}
        </div>
        <div className={`text-[10px] font-semibold mt-1.5 ${deltaColor}`}>
          {deltaText}
        </div>
      </CardContent>
    </Card>
  );
};

const CollectionMetricStrip: React.FC<CollectionMetricStripProps> = ({
  metrics,
  className = '',
}) => {
  const scoreColor = getConductScoreColor(metrics.conductScore.value);

  return (
    <div className={`grid grid-cols-1 md:grid-cols-4 gap-3 ${className}`}>
      <MetricCard
        label="Conduct Score"
        value={metrics.conductScore.value}
        delta={metrics.conductScore.delta}
        trend={metrics.conductScore.trend}
        color={scoreColor}
        invertTrend
      />
      <MetricCard
        label="Dissatisfaction Ratio"
        value={metrics.dissatisfactionRatio.value}
        unit="%"
        delta={metrics.dissatisfactionRatio.delta}
        trend={metrics.dissatisfactionRatio.trend}
      />
      <MetricCard
        label="Vulnerability Exposure"
        value={metrics.vulnerabilityExposure.value}
        unit="%"
        delta={metrics.vulnerabilityExposure.delta}
        trend={metrics.vulnerabilityExposure.trend}
        color="#580095"
      />
      <MetricCard
        label="Eligible Complaints"
        value={metrics.eligibleComplaints.value}
        unit="%"
        delta={metrics.eligibleComplaints.delta}
        trend={metrics.eligibleComplaints.trend}
        color="#92204D"
      />
    </div>
  );
};

export { CollectionMetricStrip };
export type { CollectionMetricStripProps };
